import React, { useState, useEffect, useContext, useCallback } from "react";
import axios from "axios";
import { UserContext } from "../context/UserContext";
import "./ForumPage.css";
import { formatDate } from "../utils/formatDate";

const API_BASE_URL = "https://bookmatchup.onrender.com/api";

const validReactions = [
  {
    type: "like",
    label: "Like",
    path: "/images/likedark.svg",
    selectedPath: "/images/likeselected.svg",
  },
  {
    type: "love",
    label: "Love",
    path: "/images/love.svg",
    selectedPath: "/images/lovedark.svg",
  },
  {
    type: "insightful",
    label: "Insightful",
    path: "/images/insightfuldark.svg",
    selectedPath: "/images/insightfulselected.svg",
  },
];

const ForumPage = () => {
  const { user } = useContext(UserContext);
  const formattedCategory = "Feedback";

  // ✅ State Hooks (Grouped Together)
  const [threads, setThreads] = useState([]);
  const [newThreadTitle, setNewThreadTitle] = useState("");
  const [newThreadContent, setNewThreadContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reactionLoading, setReactionLoading] = useState(false);

  // ✅ Expand State Hooks (Grouped)
  const [expandedThreads, setExpandedThreads] = useState({});
  const [expandedComments, setExpandedComments] = useState({});
  const [commentContent, setCommentContent] = useState({});
  const [replyContent, setReplyContent] = useState({});
  const [showCommentInput, setShowCommentInput] = useState({});
  const [showReplyInput, setShowReplyInput] = useState({});
  const [showNewThreadForm, setShowNewThreadForm] = useState(false);
  const [editingThread, setEditingThread] = useState(null);
  const [editingThreadContent, setEditingThreadContent] = useState("");
  const [editingComment, setEditingComment] = useState(null);
  const [editingCommentContent, setEditingCommentContent] = useState("");
  const [editingReply, setEditingReply] = useState(null);
  const [editingReplyContent, setEditingReplyContent] = useState("");

  // ✅ Fetch Threads & Nested Comments in One Call
  // ✅ Fetch Threads & Nested Comments in One Call
  const fetchThreads = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/forum/feedback/threads`
      );

      const threadsWithComments = await Promise.all(
        response.data.map(async (thread) => {
          const commentResponse = await axios.get(
            `${API_BASE_URL}/forum/${thread._id}/comments`
          );
          thread.comments = commentResponse.data || [];

          await Promise.all(
            thread.comments.map(async (comment) => {
              const repliesResponse = await axios.get(
                `${API_BASE_URL}/forum/${thread._id}/${comment._id}/replies`
              );
              comment.replies = repliesResponse.data || [];

              comment.replies.forEach((reply) => {});

              return comment;
            })
          );

          return thread;
        })
      );

      setThreads(threadsWithComments);
    } catch (error) {
      console.error("Error fetching threads:", error);
      setError("Failed to load threads.");
    } finally {
      setLoading(false);
    }
  }, []); // ✅ No more [category] dependency

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  // ✅ Post Thread
  const handlePostThread = async (e) => {
    e.preventDefault();
    if (!user || !newThreadTitle.trim() || !newThreadContent.trim()) {
      setError("Title and content are required.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_BASE_URL}/forum/${user.id}/feedback/threads`,
        { title: newThreadTitle, content: newThreadContent },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      fetchThreads();
      setNewThreadTitle("");
      setNewThreadContent("");
    } catch (error) {
      console.error("Error posting thread:", error);
      setError("Failed to post thread.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Post Comment
  const handlePostComment = async (threadId) => {
    if (!user) {
      // Instead of alert, set an error state to display a message
      setError("You must be logged in to add a comment.");
      return;
    }

    // Log the comment content before sending the request

    if (!commentContent[threadId]?.trim()) return; // Ensure the comment is not empty

    try {
      const response = await axios.post(
        `${API_BASE_URL}/forum/${threadId}/${user.id}/comments`,
        { content: commentContent[threadId] },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      console.log("Response from server:", response.data);

      // Fetch the threads after the comment is successfully posted
      fetchThreads();
      // Reset the comment content and hide the comment input field
      setCommentContent((prev) => ({ ...prev, [threadId]: "" }));
      setShowCommentInput((prev) => ({ ...prev, [threadId]: false }));

      // Clear the error message after successful comment submission
      setError("");
    } catch (error) {
      console.error("Error adding comment:", error);
      setError("Failed to add comment.");
    }
  };

  // ✅ Post Reply
  const handlePostReply = async (threadId, commentId) => {
    if (!replyContent[commentId]?.trim()) return;

    try {
      await axios.post(
        `${API_BASE_URL}/forum/${threadId}/${commentId}/${user.id}/replies`,
        { content: replyContent[commentId] },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      fetchThreads();
      setReplyContent((prev) => ({ ...prev, [commentId]: "" }));
      setShowReplyInput((prev) => ({ ...prev, [commentId]: false }));
    } catch (error) {
      console.error("Error adding reply:", error);
      setError("Failed to add reply.");
    }
  };

  const handlePostReaction = async (level, id, reactionType) => {
    if (!user || !user.id) {
      // Alert the user to log in before reacting
      alert("You must be logged in to react.");
      return;
    }

    setReactionLoading(true);

    try {
      let action = "add";

      // Find current reactions
      const updatedThreads = threads.map((thread) => {
        if (level === "thread" && thread._id === id) {
          const userReactions = thread.reactions.filter(
            (r) => r.user === user.id
          );
          const existingReaction = userReactions.find(
            (r) => r.type === reactionType
          );

          // If user already reacted with the same type, remove it
          if (existingReaction) {
            action = "remove";
            thread.reactions = thread.reactions.filter(
              (r) => !(r.user === user.id && r.type === reactionType)
            );
          }
          // If user reacted with a different type, switch reaction
          else {
            thread.reactions = thread.reactions.filter(
              (r) => r.user !== user.id
            );
            thread.reactions.push({ user: user.id, type: reactionType });
          }
        }

        if (level === "comment") {
          thread.comments = thread.comments.map((comment) => {
            if (comment._id === id) {
              const userReactions = comment.reactions.filter(
                (r) => r.user === user.id
              );
              const existingReaction = userReactions.find(
                (r) => r.type === reactionType
              );

              if (existingReaction) {
                action = "remove";
                comment.reactions = comment.reactions.filter(
                  (r) => !(r.user === user.id && r.type === reactionType)
                );
              } else {
                comment.reactions = comment.reactions.filter(
                  (r) => r.user !== user.id
                );
                comment.reactions.push({ user: user.id, type: reactionType });
              }
            }
            return comment;
          });
        }

        if (level === "reply") {
          thread.comments = thread.comments.map((comment) => ({
            ...comment,
            replies: comment.replies.map((reply) => {
              if (reply._id === id) {
                const userReactions = reply.reactions.filter(
                  (r) => r.user === user.id
                );
                const existingReaction = userReactions.find(
                  (r) => r.type === reactionType
                );

                if (existingReaction) {
                  action = "remove";
                  reply.reactions = reply.reactions.filter(
                    (r) => !(r.user === user.id && r.type === reactionType)
                  );
                } else {
                  reply.reactions = reply.reactions.filter(
                    (r) => r.user !== user.id
                  );
                  reply.reactions.push({ user: user.id, type: reactionType });
                }
              }
              return reply;
            }),
          }));
        }

        return thread;
      });

      setThreads(updatedThreads);

      // Send reaction update to backend
      await axios.post(
        `${API_BASE_URL}/forum/${level}/${id}/reaction/${reactionType}`,
        { userId: user.id, action },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
    } catch (error) {
      console.error("Error reacting:", error);
      setError("Failed to react.");
    } finally {
      setReactionLoading(false);
    }
  };

  const renderReactions = (level, id, reactions) => {
    // Flatten reactions into an array
    const reactionArray = Object.values(reactions).flat();

    // Get the reaction type of the current user
    const userReaction = reactionArray.find((r) => r.user === user?.id)?.type;

    return (
      <div className="reactions">
        {validReactions.map(({ type, label, path, selectedPath }) => {
          const count =
            reactionArray.filter((r) => r.type === type).length || 0;
          const isSelected = userReaction === type; // Check if user reacted with this type

          return (
            <button
              key={type}
              className={`reaction-button3 ${isSelected ? "active" : ""}`}
              onClick={() => handlePostReaction(level, id, type)}
              title={label}
              disabled={reactionLoading}
            >
              <img
                src={isSelected ? selectedPath : path}
                alt={label}
                className="reaction-icon"
              />
              {count}
            </button>
          );
        })}
      </div>
    );
  };

  const handleEditReply = (reply) => {
    setEditingReply(reply._id);
    setEditingReplyContent(reply.content);
  };

  const handleEditThread = (thread) => {
    setEditingThread(thread._id);
    setEditingThreadContent(thread.content);
  };

  const saveEditedThread = async (threadId) => {
    try {
      await axios.put(
        `${API_BASE_URL}/forum/thread/${threadId}/edit`,
        { content: editingThreadContent },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      setThreads((prevThreads) =>
        prevThreads.map((thread) =>
          thread._id === threadId
            ? { ...thread, content: editingThreadContent }
            : thread
        )
      );

      setEditingThread(null);
    } catch (error) {
      console.error("Error saving thread edit:", error);
    }
  };

  const canEditOrDelete = (postUserId) => {
    if (!user || !postUserId) return false;

    // Normalize user IDs for comparison
    const userIdString =
      typeof postUserId === "object"
        ? postUserId._id?.toString()
        : postUserId.toString();

    return userIdString === user.id.toString();
  };

  const handleDeleteThread = async (threadId) => {
    if (!window.confirm("Are you sure you want to delete this thread?")) return;

    try {
      await axios.delete(`${API_BASE_URL}/forum/thread/${threadId}/delete`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });

      setThreads((prevThreads) =>
        prevThreads.map((thread) =>
          thread._id === threadId
            ? {
                ...thread,
                content: "Message deleted by user",
                username: "[Deleted User]",
              }
            : thread
        )
      );
    } catch (error) {
      console.error("Error deleting thread:", error);
    }
  };

  const handleEditComment = (comment) => {
    setEditingComment(comment._id);
    setEditingCommentContent(comment.content);
  };

  const saveEditedComment = async (commentId) => {
    try {
      await axios.put(
        `${API_BASE_URL}/forum/comment/${commentId}/edit`,
        { content: editingCommentContent },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      setThreads((prevThreads) =>
        prevThreads.map((thread) => ({
          ...thread,
          comments: thread.comments.map((comment) =>
            comment._id === commentId
              ? { ...comment, content: editingCommentContent }
              : comment
          ),
        }))
      );

      setEditingComment(null);
    } catch (error) {
      console.error("Error saving comment edit:", error);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("Are you sure you want to delete this comment?"))
      return;

    try {
      await axios.delete(`${API_BASE_URL}/forum/comment/${commentId}/delete`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });

      setThreads((prevThreads) =>
        prevThreads.map((thread) => ({
          ...thread,
          comments: thread.comments.map((comment) =>
            comment._id === commentId
              ? {
                  ...comment,
                  content: "Message deleted by user",
                  username: "[Deleted User]",
                }
              : comment
          ),
        }))
      );
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  const saveEditedReply = async (replyId) => {
    try {
      await axios.put(
        `${API_BASE_URL}/forum/reply/${replyId}/edit`,
        { content: editingReplyContent },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      setThreads((prevThreads) =>
        prevThreads.map((thread) => ({
          ...thread,
          comments: thread.comments.map((comment) => ({
            ...comment,
            replies: comment.replies.map((reply) =>
              reply._id === replyId
                ? { ...reply, content: editingReplyContent }
                : reply
            ),
          })),
        }))
      );

      setEditingReply(null);
    } catch (error) {
      console.error("Error saving reply edit:", error);
    }
  };

  const handleDeleteReply = async (replyId) => {
    if (!window.confirm("Are you sure you want to delete this reply?")) return;

    try {
      await axios.delete(`${API_BASE_URL}/forum/reply/${replyId}/delete`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });

      setThreads((prevThreads) =>
        prevThreads.map((thread) => ({
          ...thread,
          comments: thread.comments.map((comment) => ({
            ...comment,
            replies: comment.replies.map((reply) =>
              reply._id === replyId
                ? {
                    ...reply,
                    content: "Message deleted by user",
                    username: "[Deleted User]",
                  }
                : reply
            ),
          })),
        }))
      );
    } catch (error) {
      console.error("Error deleting reply:", error);
    }
  };

  const toggleNewThreadForm = () => {
    setShowNewThreadForm((prev) => !prev);
  };

  const toggleRepliesVisibility = (commentId, hasReplies) => {
    setExpandedComments((prev) => ({
      ...prev,
      [commentId]: !prev[commentId], // ✅ Toggle replies section
    }));

    if (!hasReplies) {
      setShowReplyInput((prev) => ({
        ...prev,
        [commentId]: true, // ✅ Open reply input if no replies exist
      }));
    }
  };

  const toggleReplyInputVisibility = (commentId) => {
    setShowReplyInput((prevState) => ({
      ...prevState,
      [commentId]: !prevState[commentId], // ✅ Toggle input field visibility
    }));
  };

  const toggleThreadExpand = (threadId, hasComments) => {
    setExpandedThreads((prev) => ({
      ...prev,
      [threadId]: !prev[threadId], // ✅ Toggle thread expansion
    }));

    if (!hasComments) {
      setShowCommentInput((prev) => ({
        ...prev,
        [threadId]: true, // ✅ Open comment input if no comments exist
      }));
    }
  };

  const toggleCommentInputVisibility = (threadId, hasComments) => {
    setExpandedThreads((prev) => ({
      ...prev,
      [threadId]: true, // ✅ Ensure the thread expands immediately
    }));

    setShowCommentInput((prev) => ({
      ...prev,
      [threadId]: hasComments ? !prev[threadId] : true, // ✅ If no comments, open input directly
    }));
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="forum-container">
      <h1 className="forum-title">{formattedCategory} Forum</h1>
      <div className="mini-void"></div>
      {user && (
        <div className="new-thread-section">
          <button className="forum-button" onClick={toggleNewThreadForm}>
            {showNewThreadForm ? "Hide Thread Form" : "Post a New Thread"}
          </button>

          <div className="mini-void"></div>

          {showNewThreadForm && (
            <div className="new-thread-form">
              <h3 className="thread-post-3">Post a New Thread</h3>
              <form onSubmit={handlePostThread}>
                <input
                  className="thread-title-input"
                  type="text"
                  placeholder="Thread Title"
                  value={newThreadTitle}
                  onChange={(e) => setNewThreadTitle(e.target.value)}
                />
                <textarea
                  placeholder="What's on your mind?"
                  value={newThreadContent}
                  onChange={(e) => setNewThreadContent(e.target.value)}
                  style={{
                    width: "100%",
                    height: "150px",
                    padding: "10px",
                    fontSize: "16px",
                  }}
                />
                <button
                  type="submit"
                  className="forum-button"
                  disabled={loading}
                >
                  {loading ? "Posting..." : "Post Thread"}
                </button>
                <div className="micro-void"></div>
              </form>
            </div>
          )}
        </div>
      )}

      {threads.map((thread) => (
        <div key={thread._id} className="thread-card">
          <h4 className="thread-title">{thread.title}</h4>
          <p className="thread-author">
            {thread.username} -{" "}
            <span className="forum-date">{formatDate(thread.timestamp)}</span>
          </p>

          {/* Edit/Delete Controls for Thread */}
          {canEditOrDelete(thread.user) && (
            <div className="edit-delete-buttons">
              {editingThread === thread._id ? (
                <>
                  <textarea
                    value={editingThreadContent}
                    onChange={(e) => setEditingThreadContent(e.target.value)}
                  />
                  <button
                    className="forum-button-outline"
                    onClick={() => saveEditedThread(thread._id)}
                  >
                    Save
                  </button>
                  <button
                    className="forum-button-outline"
                    onClick={() => setEditingThread(null)}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="forum-button-outline"
                    onClick={() => handleEditThread(thread)}
                  >
                    Edit
                  </button>
                  <button
                    className="forum-button"
                    onClick={() => handleDeleteThread(thread._id)}
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          )}
          <div className="micro-void"></div>

          {/* Thread Content */}
          <div
            className="thread-content"
            style={{
              whiteSpace: "pre-wrap",
              padding: "10px",
              backgroundColor: "#f1f1f1",
              border: "1px solid #ccc",
              borderRadius: "5px",
              minHeight: "100px",
              fontSize: "16px",
              lineHeight: "1.5",
            }}
          >
            {thread.content}
          </div>

          {renderReactions("thread", thread._id, thread.reactions)}

          <button
            className="forum-button"
            onClick={() =>
              toggleThreadExpand(thread._id, thread.comments.length > 0)
            }
          >
            {thread.comments.length === 0
              ? "Add Comment"
              : expandedThreads[thread._id]
              ? `Hide Comments (${thread.comments.length})`
              : `See Comments (${thread.comments.length})`}
          </button>

          {expandedThreads[thread._id] && (
            <div className="comments-section">
              {thread.comments.map((comment) => (
                <div key={comment._id} className="comment-card">
                  {/* Comment Content */}
                  <div
                    className="comment-content"
                    style={{
                      whiteSpace: "pre-wrap",
                      padding: "10px",
                      backgroundColor: "#e9e9e9",
                      border: "1px solid #ccc",
                      borderRadius: "5px",
                      fontSize: "16px",
                      lineHeight: "1.5",
                    }}
                  >
                    {editingComment === comment._id ? (
                      <textarea
                        value={editingCommentContent}
                        onChange={(e) =>
                          setEditingCommentContent(e.target.value)
                        }
                      />
                    ) : (
                      comment.content
                    )}
                  </div>

                  <p className="comment-author">
                    {comment.username} -{" "}
                    <span className="forum-date">
                      {formatDate(comment.timestamp)}
                    </span>
                  </p>

                  {/* Edit/Delete Controls for Comment */}
                  {canEditOrDelete(comment.user) && (
                    <div className="edit-delete-buttons">
                      {editingComment === comment._id ? (
                        <>
                          <textarea
                            value={editingCommentContent}
                            onChange={(e) =>
                              setEditingCommentContent(e.target.value)
                            }
                          />
                          <button
                            className="forum-button-outline"
                            onClick={() => saveEditedComment(comment._id)}
                          >
                            Save
                          </button>
                          <button
                            className="forum-button"
                            onClick={() => setEditingComment(null)}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="forum-button-outline"
                            onClick={() => handleEditComment(comment)}
                          >
                            Edit
                          </button>
                          <button
                            className="forum-button"
                            onClick={() => handleDeleteComment(comment._id)}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  )}

                  {renderReactions("comment", comment._id, comment.reactions)}

                  <button
                    className="forum-button"
                    onClick={() =>
                      toggleRepliesVisibility(
                        comment._id,
                        comment.replies.length > 0
                      )
                    }
                  >
                    {comment.replies.length === 0
                      ? "Add Reply"
                      : expandedComments[comment._id]
                      ? `Hide Replies (${comment.replies.length})`
                      : `See Replies (${comment.replies.length})`}
                  </button>

                  {expandedComments[comment._id] && (
                    <div className="replies-section">
                      {comment.replies.map((reply) => (
                        <div key={reply._id} className="reply-card">
                          {/* Reply Content */}
                          <div
                            className="reply-content"
                            style={{
                              whiteSpace: "pre-wrap",
                              padding: "10px",
                              backgroundColor: "#f4f4f4",
                              border: "1px solid #ccc",
                              borderRadius: "5px",
                              fontSize: "16px",
                              lineHeight: "1.5",
                            }}
                          >
                            {editingReply === reply._id ? (
                              <textarea
                                value={editingReplyContent}
                                onChange={(e) =>
                                  setEditingReplyContent(e.target.value)
                                }
                              />
                            ) : (
                              reply.content
                            )}
                          </div>

                          <p className="reply-author">
                            {reply.username} -{" "}
                            <span className="forum-date">
                              {formatDate(reply.timestamp)}
                            </span>
                          </p>
                          {/* Edit/Delete Controls for Reply */}
                          {canEditOrDelete(reply.user) && (
                            <div className="edit-delete-buttons">
                              {editingReply === reply._id ? (
                                <>
                                  <textarea
                                    value={editingReplyContent}
                                    onChange={(e) =>
                                      setEditingReplyContent(e.target.value)
                                    }
                                  />
                                  <button
                                    className="forum-button-outline"
                                    onClick={() => saveEditedReply(reply._id)}
                                  >
                                    Save
                                  </button>
                                  <button
                                    className="forum-button"
                                    onClick={() => setEditingReply(null)}
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    className="forum-button-outline"
                                    onClick={() => handleEditReply(reply)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    className="forum-button"
                                    onClick={() => handleDeleteReply(reply._id)}
                                  >
                                    Delete
                                  </button>
                                </>
                              )}
                            </div>
                          )}

                          {renderReactions("reply", reply._id, reply.reactions)}
                        </div>
                      ))}

                      {user && (
                        <>
                          {showReplyInput[comment._id] ? (
                            <>
                              <textarea
                                placeholder="Write a reply..."
                                value={replyContent[comment._id] || ""}
                                onChange={(e) =>
                                  setReplyContent((prev) => ({
                                    ...prev,
                                    [comment._id]: e.target.value,
                                  }))
                                }
                              />
                              <button
                                className="forum-button"
                                onClick={() =>
                                  handlePostReply(thread._id, comment._id)
                                }
                              >
                                Submit Reply
                              </button>
                            </>
                          ) : (
                            <button
                              className="forum-button"
                              onClick={() =>
                                toggleReplyInputVisibility(comment._id)
                              }
                            >
                              Add Reply
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {user && (
                <>
                  {showCommentInput[thread._id] ? (
                    <>
                      <textarea
                        placeholder="Add a comment..."
                        value={commentContent[thread._id] || ""}
                        onChange={(e) =>
                          setCommentContent((prev) => ({
                            ...prev,
                            [thread._id]: e.target.value,
                          }))
                        }
                      />

                      {!user && (
                        <div className="error-message">
                          You must be logged in to add a comment.
                        </div>
                      )}

                      <button
                        className="forum-button"
                        onClick={() => handlePostComment(thread._id)}
                        disabled={!user || !commentContent[thread._id]?.trim()}
                      >
                        Submit Comment
                      </button>
                    </>
                  ) : (
                    <button
                      className="forum-button"
                      onClick={() =>
                        toggleCommentInputVisibility(
                          thread._id,
                          thread.comments.length > 0
                        )
                      }
                    >
                      {thread.comments.length === 0
                        ? "Add Comment"
                        : showCommentInput[thread._id]
                        ? "Cancel"
                        : "Add Comment"}
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ForumPage;
